// ── Model Registry ────────────────────────────────────────────────────────────
// Single source of truth for what each integrated model supports.
//
// Used to:
//   1. Dynamically generate the LLM decomposer system prompt (property registry +
//      domain classification rules) so the prompt is always in sync with the stack.
//   2. Drive specResolver and modelRouter without hardcoding capabilities in multiple places.
//
// To register a new model:
//   Add one entry to MODEL_REGISTRY below. The prompt, resolver, and router
//   will automatically pick up its domains, family tags, and conditionable properties.

export const MODEL_REGISTRY = [
  {
    id: 'mattergen',
    name: 'MatterGen',
    description: 'Diffusion-based inorganic crystal generator (periodic solid-state materials)',
    domains: ['inorganic_crystal'],
    // Family tags the LLM should emit to route to this model
    family_tags: ['inorganic_crystal', 'alloy', 'surface_interface'],
    conditionable_properties: [
      { name: 'band_gap',            type: 'electronic',     unit: 'eV',        note: 'DFT-computed' },
      { name: 'bulk_modulus',        type: 'mechanical',     unit: 'GPa',       note: 'ML-predicted' },
      { name: 'energy_above_hull',   type: 'thermodynamic',  unit: 'eV/atom',   note: 'thermodynamic stability; lower = more stable' },
      { name: 'magnetic_density',    type: 'magnetic',       unit: 'μB/Å³',     note: 'magnetic density conditioning' },
      { name: 'space_group',         type: 'structural',     unit: '',          note: 'space group number (1–230)' },
    ],
    // Properties the system can derive from outputs (not user-conditionable but scoreable)
    derived_properties: [
      { name: 'density_est',              type: 'structural',   unit: 'g/cm³' },
      { name: 'packing_fraction_est',     type: 'structural',   unit: '' },
      { name: 'min_interatomic_distance', type: 'structural',   unit: 'Å' },
      { name: 'mean_electronegativity',   type: 'chemical',     unit: '' },
    ],
    // What inputs the service accepts (informational, for docs + future form generation)
    inputs: {
      elements_in:   { type: 'string[]', description: 'Element symbols to include' },
      elements_out:  { type: 'string[]', description: 'Element symbols to exclude' },
      num_candidates:{ type: 'integer',  description: 'Candidates to generate per variant (1 to the plan max per run, at most 1024; the manual form offers the base-8 ladder 8 to 1024)' },
      chunk_size:    { type: 'integer',  description: 'Candidates per MatterGen job (the base-8 ladder 8 to 256, capped by the plan max; default 64)' },
      space_group:   { type: 'integer',  description: 'Target space group number (1–230)' },
    },
  },

  {
    id: 'fm4m',
    name: 'IBM FM4M',
    description: 'IBM Foundation Model for Materials — molecular embeddings, similarity, and de-novo generation',
    domains: ['polymer', 'small_molecule'],
    family_tags: ['polymer', 'small_molecule'],
    conditionable_properties: [],  // FM4M does unconditioned generation + featurization
    derived_properties: [
      { name: 'embedding_vector', type: 'representation', unit: '' },
    ],
    inputs: {
      seed_smiles:  { type: 'string[]', description: 'Seed SMILES strings (optional; used for embedding/similarity)' },
      model:        { type: 'enum',     description: 'bart | mhg | smi-ted | mol-xl' },
      chunk_size:   { type: 'integer',  description: 'SMILES per featurization job (1 to 200, default 25)' },
    },
  },

  {
    id: 'gt4sd',
    name: 'GT4SD',
    description: 'IBM GT4SD generative toolkit — the Regression Transformer for property-conditioned small molecule generation, plus a HuggingFace causal-LM path that needs your own weights',
    domains: ['small_molecule'],
    family_tags: ['small_molecule'],
    // M1 (2026-07-08 audit): ONLY properties the deployed Regression Transformer
    // checkpoint actually conditions. Every RT row sends crippen_logp as its
    // algorithm_version (Part 1a, buildRow), so advertising
    // qed/tpsa/sa_score/binding_affinity/solubility here produced silently
    // meaningless conditioning. Re-add a property ONLY together with its wire token
    // under that checkpoint in GT4SD_RT_PROPERTY_TOKENS
    // (src/config/gt4sdCheckpoints.js). Other molecular targets route to FM4M's
    // over-generate + RDKit-filter path instead.
    //
    // GT4SD RT Part D: crippen_logp is a LOCAL artifact id resolved under
    // GT4SD_RT_CKPT_DIR, not a Hugging Face Hub id. The offered range is the
    // intersection of the artifact's own declared range ([-5.0, 18.17]) with the
    // feasible range for logP in properties.js, and every generated molecule comes
    // back with its logP measured by RDKit.
    conditionable_properties: [
      { name: 'logP',             type: 'molecular',  unit: '',           note: 'octanol-water partition coefficient; conditionable from -5 to 10, though targets above about 3.4 are rarely reached from a seed molecule' },
    ],
    derived_properties: [
      { name: 'molecular_weight',  type: 'molecular', unit: 'Da' },
      { name: 'hbd',               type: 'molecular', unit: '',  note: 'H-bond donors' },
      { name: 'hba',               type: 'molecular', unit: '',  note: 'H-bond acceptors' },
      { name: 'rotatable_bonds',   type: 'molecular', unit: '' },
      { name: 'aromatic_rings',    type: 'molecular', unit: '' },
      { name: 'fraction_sp3',      type: 'molecular', unit: '' },
      { name: 'ro5_violations',    type: 'molecular', unit: '',  note: 'Lipinski rule-of-5 violations' },
    ],
    inputs: {
      algorithm:        { type: 'enum',    description: 'regression_transformer (built-in) | hugging_face (your own weights only)' },
      algorithm_version:{ type: 'string',  description: 'Allowlisted local checkpoint id (src/config/gt4sdCheckpoints.js); empty selects the service default' },
      target_smiles:    { type: 'string',  description: 'Seed SMILES for optimization (optional)' },
      target_property:  { type: 'string',  description: 'Property name to condition on' },
      target_value:     { type: 'number',  description: 'Target value for the conditioned property; required for regression_transformer, which has no default' },
      n_samples:        { type: 'integer', description: 'Number of molecules to generate (1–200)' },
    },
  },
];

// ── Derived helpers ───────────────────────────────────────────────────────────

/**
 * All unique family tags the LLM may emit, derived from the registry.
 * Used to keep the prompt's family tag list in sync with what's actually registered.
 */
export function getRegisteredFamilyTags() {
  const tags = new Set(['composite', 'unknown']); // composite is cross-model, unknown is fallback
  for (const m of MODEL_REGISTRY) {
    for (const t of m.family_tags) tags.add(t);
  }
  return Array.from(tags).sort();
}

/**
 * All unique conditionable property IDs across all models.
 * Used to populate the LLM property registry section.
 */
export function getAllConditionableProperties() {
  const seen = new Map(); // name → entry (first model wins for dedup)
  for (const m of MODEL_REGISTRY) {
    for (const p of m.conditionable_properties) {
      if (!seen.has(p.name)) seen.set(p.name, { ...p, model: m.name });
    }
  }
  return Array.from(seen.values());
}

/**
 * All unique derived/scoreable property IDs across all models.
 */
export function getAllDerivedProperties() {
  const seen = new Map();
  for (const m of MODEL_REGISTRY) {
    for (const p of (m.derived_properties || [])) {
      if (!seen.has(p.name)) seen.set(p.name, { ...p, model: m.name });
    }
  }
  return Array.from(seen.values());
}

/**
 * Build the property registry section and domain classification rules
 * to inject into the LLM system prompt.
 *
 * @param {Array} models - Subset of MODEL_REGISTRY to include. Defaults to the full registry.
 *   Pass a filtered list (e.g. only models whose domains match the pre-resolved domain)
 *   to keep the prompt bounded as the model zoo grows.
 */
export function buildLLMModelContext(models = MODEL_REGISTRY) {
  // ── Property registry ────────────────────────────────────────────────────
  const propertyLines = models.map(m => {
    if (m.conditionable_properties.length === 0) {
      return `- ${m.name} [${m.domains.join('/')}]: no directly conditionable properties (generates via embedding/sampling)`;
    }
    const propList = m.conditionable_properties.map(p => {
      const parts = [p.name];
      if (p.unit) parts.push(`(${p.unit})`);
      if (p.note) parts.push(`— ${p.note}`);
      return parts.join(' ');
    }).join(', ');
    return `- ${m.name} [${m.domains.join('/')}]: ${propList}`;
  }).join('\n');

  // Derived/scoreable properties (not conditionable but the LLM should know about them
  // so it can emit them as objectives even when no model directly conditions on them)
  const derivedByType = {};
  for (const m of models) {
    for (const p of (m.derived_properties || [])) {
      const t = p.type;
      if (!derivedByType[t]) derivedByType[t] = [];
      if (!derivedByType[t].includes(p.name)) derivedByType[t].push(p.name);
    }
  }
  const derivedLines = Object.entries(derivedByType)
    .map(([type, names]) => `- ${type}: ${names.join(', ')}`)
    .join('\n');

  // ── Domain classification rules ───────────────────────────────────────────
  const domainRules = models.map(m => {
    const tagList = m.family_tags.map(t => `"${t}"`).join(', ');
    return `  - ${m.description} → [${tagList}]`;
  }).join('\n');

  // ── Family tag enum — always derived from full registry so the LLM knows all valid tags ──
  const allTags = getRegisteredFamilyTags().map(t => `"${t}"`).join(', ');

  return {
    propertyRegistrySection: `Property registry — conditionable by model:
${propertyLines}

Scoreable (derived from outputs, not directly conditioned on):
${derivedLines}`,

    domainClassificationSection: `Domain classification rules:
- Set generation_spec.system.family based on what the user is asking for:
${domainRules}
  - Multi-domain requests (e.g. ceramic + polymer composite, body armor) → ["inorganic_crystal", "polymer"] or use "composite"
  - When domain is truly ambiguous → ["unknown"]
- Valid family tags: ${allTags}`,
  };
}
